/**
 * Social Security claiming explorer (spec §8): the 2-D claiming-age grid as a
 * heat map — the SHAPE of the surface tells the user how much the decision
 * matters, which is often more useful than the argmax. Click a cell to apply.
 */
import { useMemo, useState } from 'react';
import type { Plan } from '../model/types';
import { claimingGrid, type ClaimingCell } from '../engine/optimizer';
import { fmtCompactUsd } from './format';

interface Props {
  plan: Plan;
  onChange: (updated: Plan) => void;
}

type Metric = 'endingNetWorth' | 'pvBenefits';

export function SocialSecurityExplorer({ plan, onChange }: Props) {
  const [metric, setMetric] = useState<Metric>('endingNetWorth');

  const grid = useMemo(() => claimingGrid(plan), [plan]);
  const { personIds, ages, cells, best, current } = grid;
  if (personIds.length === 0) return null;

  const people = plan.household.people;
  const nameOf = (pid: string) => people.find((p) => p.id === pid)?.label ?? pid;
  const isCouple = personIds.length === 2;

  const flat = cells.flat();
  const values = flat.map((c) => c[metric]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const currentKey = JSON.stringify(current.agesByPerson);
  const bestKey = JSON.stringify(best.agesByPerson);
  const delta = best.endingNetWorth - current.endingNetWorth;

  function apply(cell: ClaimingCell) {
    const next = structuredClone(plan);
    next.decisions.claimingAgeOverride = cell.agesByPerson;
    // Mirror into the person inputs so the UI shows the chosen ages.
    for (const [pid, age] of Object.entries(cell.agesByPerson)) {
      const person = next.household.people.find((p) => p.id === pid);
      if (person) person.socialSecurity.claimingAge = age;
    }
    onChange(next);
  }

  return (
    <section className="card" aria-label="Social Security claiming explorer">
      <h2 className="section-title">
        Social Security: when to claim
        <span className="note">
          <span className="seg" role="group" aria-label="Optimizer metric">
            <button aria-pressed={metric === 'endingNetWorth'} onClick={() => setMetric('endingNetWorth')}>
              Ending net worth
            </button>
            <button aria-pressed={metric === 'pvBenefits'} onClick={() => setMetric('pvBenefits')}>
              Lifetime benefits (PV)
            </button>
          </span>
        </span>
      </h2>

      <p style={{ color: 'var(--ink-secondary)', marginTop: 0 }}>
        {delta > 1000 ? (
          <>
            Best combination found: {personIds.map((pid) => `${nameOf(pid)} at ${best.agesByPerson[pid]}`).join(', ')} —
            worth about <strong>{fmtCompactUsd(delta)}</strong> more ending net worth than the current choice.
          </>
        ) : (
          <>Your current claiming choice is at or near the best this model finds.</>
        )}{' '}
        The spread across the whole grid is {fmtCompactUsd(max - min)} — that's how much this
        decision matters under these assumptions.
      </p>

      <div className="heatmap-wrap">
        <table className="heatmap" aria-label="Claiming age grid">
          <thead>
            <tr>
              <th scope="col">{isCouple ? `${nameOf(personIds[0]!)} ↓ · ${nameOf(personIds[1]!)} →` : ''}</th>
              {(isCouple ? ages : ages).map((a) => (
                <th key={a} scope="col">{isCouple ? a : `Claim at ${a}`}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cells.map((row, i) => (
              <tr key={i}>
                <th scope="row">{isCouple ? ages[i] : nameOf(personIds[0]!)}</th>
                {row.map((cell, j) => {
                  const t = (cell[metric] - min) / span;
                  const key = JSON.stringify(cell.agesByPerson);
                  const isBest = key === bestKey;
                  const isCurrent = key === currentKey;
                  return (
                    <td key={j}>
                      <button
                        className={`heatcell${isCurrent ? ' current' : ''}`}
                        style={{ ['--heat' as string]: `${Math.round(8 + t * 72)}%` }}
                        onClick={() => apply(cell)}
                        title={`${personIds.map((pid) => `${nameOf(pid)}: ${cell.agesByPerson[pid]}`).join(' · ')}
Ending net worth ${fmtCompactUsd(cell.endingNetWorth)} · PV benefits ${fmtCompactUsd(cell.pvBenefits)}${cell.depletionYear ? ` · runs out ${cell.depletionYear}` : ''}`}
                        aria-label={`Claim ${personIds.map((pid) => `${nameOf(pid)} at ${cell.agesByPerson[pid]}`).join(', ')}: ${fmtCompactUsd(cell[metric])}`}
                      >
                        {isBest ? '★' : isCurrent ? '●' : ''}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hint">
        ★ best under this metric · ● current choice · darker = better · click any cell to apply it.
        Delaying the higher earner to 70 often wins for couples — it buys the largest possible
        survivor benefit for whichever spouse lives longer.
      </p>
    </section>
  );
}
